import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { getMunicipalityById, Municipality, getYearlyDataForYear, getYearlyTotal, FEE_CATEGORIES, CATEGORY_FIELD } from "@/lib/data";
import { useFeeCategories } from "@/contexts/FeeCategoryContext";
import { getLatestYear } from "@/config/years";
import YearSelector from "@/components/YearSelector";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Info, AlertTriangle, ArrowLeft } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ComparisonDetailPage = () => {
  const { enabledCategories } = useFeeCategories();
  const visibleCategories = FEE_CATEGORIES.filter(c => enabledCategories.includes(c.id));
  const [searchParams] = useSearchParams();
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("table");
  const [selectedYear, setSelectedYear] = useState<number>(() => getLatestYear());

  useEffect(() => {
    const fetchMunicipalities = async () => {
      try {
        setIsLoading(true);
        const municipalityIds = searchParams.get("ids")?.split(",") || [];
        
        if (municipalityIds.length === 0) {
          toast.error("Ingen kommuner valgt for sammenligning");
          return;
        }
        
        const municipalityPromises = municipalityIds.map(id => getMunicipalityById(id));
        const municipalityResults = await Promise.all(municipalityPromises);
        
        // Filter ut nullverdier (kommuner som ikke finnes)
        const validMunicipalities = municipalityResults.filter(
          (municipality): municipality is Municipality => !!municipality
        );
        
        if (validMunicipalities.length === 0) {
          toast.error("Ingen gyldige kommuner funnet");
          return;
        }
        
        setMunicipalities(validMunicipalities);
      } catch (error) {
        console.error("Error fetching municipalities:", error);
        toast.error("Kunne ikke hente kommunedata");
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMunicipalities();
  }, [searchParams]);

  // Hjelper for formatering av beløp
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("nb-NO");
  };

  const getDataForYear = (municipality: Municipality) => {
    const yearData = getYearlyDataForYear(municipality, selectedYear);
    if (!yearData) {
      const latest = [...(municipality.historicalData || [])].sort((a, b) => b.year - a.year)[0];
      return latest ?? { waterFee: 0, sewageFee: 0, wasteFee: 0, propertyTax: 0, chimneySweepFee: 0, totalFees: 0 };
    }
    return yearData;
  };

  // Data for sammenligning av avgifter
  const getChartData = () => {
    return visibleCategories.map((category) => ({
      name: category.name,
      ...municipalities.reduce((acc, m) => {
        acc[m.name] = getDataForYear(m)[CATEGORY_FIELD[category.id]];
        return acc;
      }, {} as Record<string, number>)
    }));
  };

  // Generere farger for grafen
  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

  // Data for totale avgifter
  const getTotalChartData = () => {
    return municipalities.map((m, i) => {
      const latestData = getDataForYear(m);
      return {
        name: m.name,
        total: getYearlyTotal(latestData, enabledCategories),
        fill: COLORS[i % COLORS.length]
      };
    });
  };

  // Beregn høyeste total for graf-skalering
  const getMaxTotal = () => {
    if (municipalities.length === 0) return 20000;

    return Math.max(...municipalities.map(m => getYearlyTotal(getDataForYear(m), enabledCategories)));
  };

  const names = municipalities.map(m => m.name).join(", ");
  return (
    <div className="min-h-screen flex flex-col bg-softWhite">
      <Seo
        title={municipalities.length > 0 ? `Sammenligning: ${names} | Kommunale avgifter` : "Sammenligning | Kommunale avgifter"}
        description={municipalities.length > 0 ? `Sammenlign kommunale avgifter for ${names}. Vann, avløp, renovasjon og septiktømming side om side.` : "Sammenlign kommunale avgifter mellom valgte kommuner."}
        path={`/sammenligne-detalj${searchParams.toString() ? `?${searchParams.toString()}` : ""}`}
      />
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero – samme kortstil som forsiden */}
        <div className="mb-8 py-6 px-5 md:px-6 rounded-2xl bg-gradient-to-br from-nordicBlue/5 to-transparent border border-nordicBlue/10">
          <nav className="flex items-center gap-2 text-sm text-warmGray mb-4">
            <Link to="/" className="flex items-center gap-1.5 hover:text-nordicBlue transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" />
              Forside
            </Link>
            <span>/</span>
            <Link to="/sammenlign" className="hover:text-nordicBlue transition-colors">Sammenlign</Link>
            <span>/</span>
            <span className="text-nordicBlue font-semibold">Detaljert sammenligning</span>
          </nav>
          <div className="flex flex-wrap justify-between items-start gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-deepCharcoal mb-2">Detaljert sammenligning</h1>
              {!isLoading && municipalities.length > 0 && (
                <p className="text-warmGray">
                  {municipalities.map(m => m.name).join(", ")}
                  {selectedYear !== getLatestYear() && ` — tall for ${selectedYear}`}
                </p>
              )}
            </div>
            {!isLoading && municipalities.length > 0 && (
              <YearSelector value={selectedYear} onChange={setSelectedYear} />
            )}
          </div>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nordicBlue"></div>
          </div>
        ) : municipalities.length === 0 ? (
          <Alert className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Ingen kommuner funnet</AlertTitle>
            <AlertDescription>
              Kunne ikke finne kommunene for sammenligning. Vennligst gå tilbake og velg kommuner på nytt.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
              <TabsList>
                <TabsTrigger value="table">Tabell</TabsTrigger>
                <TabsTrigger value="bar">Søylediagram</TabsTrigger>
                <TabsTrigger value="total">Totalkostnader</TabsTrigger>
              </TabsList>
              
              <TabsContent value="table" className="mt-4">
                <div className="rounded-lg shadow-sm border border-slate-100 bg-white overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Avgiftstype</TableHead>
                        {municipalities.map(m => (
                          <TableHead key={m.id} className="text-right">{m.name}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleCategories.map((category) => (
                        <TableRow key={category.id}>
                          <TableCell className="font-medium">{category.name}</TableCell>
                          {municipalities.map(m => (
                            <TableCell key={m.id} className="text-right">
                              {formatCurrency(getDataForYear(m)[CATEGORY_FIELD[category.id]])}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell className="font-bold">Totalt</TableCell>
                        {municipalities.map(m => (
                          <TableCell key={m.id} className="text-right font-bold">{formatCurrency(getYearlyTotal(getDataForYear(m), enabledCategories))}</TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
              
              <TabsContent value="bar" className="mt-4">
                <div className="rounded-lg shadow-sm border border-slate-100 bg-white p-4 md:p-5">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={getChartData()}
                      margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Legend />
                      {municipalities.map((m, index) => (
                        <Bar 
                          key={m.id} 
                          dataKey={m.name} 
                          fill={COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
              
              <TabsContent value="total" className="mt-4">
                <div className="rounded-lg shadow-sm border border-slate-100 bg-white p-4 md:p-5">
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart
                      data={getTotalChartData()}
                      margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis domain={[0, getMaxTotal() * 1.1]} />
                      <Tooltip formatter={(value) => formatCurrency(value as number)} />
                      <Legend />
                      <Bar dataKey="total" name="Totale avgifter" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
            </Tabs>

            <Alert className="mb-4 bg-blue-50 border-blue-200">
              <Info className="h-4 w-4 text-blue-500" />
              <AlertTitle>Om datagrunnlaget</AlertTitle>
              <AlertDescription>
                Alle beløp er oppgitt i norske kroner (NOK) per år for en bolig på 120m². 
                Kilde: Statistisk sentralbyrå (SSB).
              </AlertDescription>
            </Alert>
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default ComparisonDetailPage; 